import {
  type TaskCreateDto,
  type TaskStatus,
  useSalesCompleteTask,
  useSalesCreateTask,
  useSalesDeleteTask,
  useSalesGetMyTasks,
  useSalesGetTasksDueSoon,
  useSalesUpdateTask,
} from "@crowdcoursing/api/openapi";
import { skipToken } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";

import { useSalesBubbleId } from "./useSalesBubbleId";
import { useToast } from "./useToast";
import { useSelector } from "./useTypedRedux";

export interface TasksFilter {
  status?: TaskStatus;
  dueBefore?: string;
}

export const useSalesTasks = () => {
  const toast = useToast();
  const salesBubbleId = useSalesBubbleId();
  const userId = useSelector((state) => state.user?.id);
  if (!userId) {
    throw new Error("User must be logged in to use sales tasks");
  }
  const [filters, setFilters] = useState<TasksFilter>({ status: 1 }); // Todo
  const [daysUntilDue, setDaysUntilDue] = useState(7);

  // Fetch tasks
  const { data: rawMyTasks = [], refetch: refetchMyTasks } = useSalesGetMyTasks(
    salesBubbleId
      ? {
          pathParams: { bubbleId: salesBubbleId },
          queryParams: {
            ...filters,
          },
        }
      : skipToken,
  );

  const {
    data: rawTasksDueSoon = [],
    status: tasksDueSoonStatus,
    refetch: refetchTasksDueSoon,
  } = useSalesGetTasksDueSoon(
    salesBubbleId && daysUntilDue > 0
      ? {
          pathParams: { bubbleId: salesBubbleId },
          queryParams: { days: daysUntilDue },
        }
      : skipToken,
    {
      enabled: Boolean(salesBubbleId) && daysUntilDue > 0,
    },
  );

  // Transform tasks to include required fields
  const myTasks = rawMyTasks
    .filter(
      (task): task is NonNullable<typeof task> & { id: string; title: string } =>
        task?.id !== undefined && task?.title !== null,
    )
    .map((task) => {
      // Debug individual task
      console.log(`Task ${task.id} prospectName:`, task.prospectName);

      return {
        // Include other fields that might be needed
        assignedToFirstName: task.assignedToFirstName,

        assignedToLastName: task.assignedToLastName,

        assignedToUserId: task.assignedToUserId,

        contactId: task.contactId,

        description: task.description,

        dueAt: task.dueAt,

        id: task.id,

        priority: task.priority,

        // Make sure to include prospectId and prospectName
        prospectId: task.prospectId,

        prospectName: task.prospectName,

        reminderAt: task.reminderAt,

        status: task.status,
        title: task.title,
      };
    });

  // Debug due soon tasks
  console.log("Raw tasks due soon from API:", rawTasksDueSoon);

  const tasksDueSoon = rawTasksDueSoon
    .filter(
      (task): task is NonNullable<typeof task> & { id: string; title: string } =>
        task?.id !== undefined && task?.title !== null,
    )
    .map((task) => {
      // Debug individual task
      console.log(`Task due soon ${task.id} prospectName:`, task.prospectName);

      return {
        ...task,
        // Make sure these fields are explicitly included
        prospectId: task.prospectId,
        prospectName: task.prospectName,
      };
    });

  // Task mutations
  const { mutate: createTask } = useSalesCreateTask({
    onError: () => {
      toast.error("Failed to create task");
    },
    onSuccess: () => {
      Promise.all([refetchMyTasks(), refetchTasksDueSoon()]);
      toast.success("Task created successfully");
    },
  });

  const { mutate: updateTask } = useSalesUpdateTask({
    onError: () => {
      toast.error("Failed to update task");
    },
    onSuccess: () => {
      Promise.all([refetchMyTasks(), refetchTasksDueSoon()]);
      toast.success("Task updated successfully");
    },
  });

  const { mutate: deleteTask } = useSalesDeleteTask({
    onError: () => {
      toast.error("Failed to delete task");
    },
    onSuccess: () => {
      Promise.all([refetchMyTasks(), refetchTasksDueSoon()]);
      toast.success("Task deleted");
    },
  });

  const { mutate: completeTask } = useSalesCompleteTask({
    onError: () => {
      toast.error("Failed to complete task");
    },
    onSuccess: () => {
      Promise.all([refetchMyTasks(), refetchTasksDueSoon()]);
      toast.success("Task completed");
    },
  });

  const handleCreateTask = useCallback(
    (values: TaskCreateDto, prospectId: string) => {
      if (!salesBubbleId) {
        return;
      }

      createTask({
        body: {
          ...values,
          assignedToUserId: values.assignedToUserId || userId,
        },
        pathParams: { bubbleId: salesBubbleId, id: prospectId },
      });
    },
    [createTask, userId, salesBubbleId],
  );

  const handleCompleteTask = useCallback(
    (taskId: string) => {
      if (!salesBubbleId) {
        return;
      }

      completeTask({
        pathParams: { bubbleId: salesBubbleId, taskId },
      });
    },
    [completeTask, salesBubbleId],
  );

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      if (!salesBubbleId) {
        return;
      }

      deleteTask({
        pathParams: { bubbleId: salesBubbleId, taskId },
      });
    },
    [deleteTask, salesBubbleId],
  );

  const handleReassignTask = useCallback(
    (taskId: string, assignedToUserId: string) => {
      if (!salesBubbleId) {
        return;
      }

      const task = [...rawMyTasks, ...rawTasksDueSoon].find((t) => t.id === taskId);
      if (!task) {
        toast.error("Task not found");
        return;
      }

      updateTask({
        body: {
          assignedToUserId,
          contactId: task.contactId,
          description: task.description,
          dueAt: task.dueAt,
          priority: task.priority,
          reminderAt: task.reminderAt,
          status: task.status,
          title: task.title,
        },
        pathParams: { bubbleId: salesBubbleId, taskId },
      });
    },
    [updateTask, rawMyTasks, rawTasksDueSoon, toast, salesBubbleId],
  );

  const handleSnoozeTask = useCallback(
    (taskId: string) => {
      if (!salesBubbleId) {
        return;
      }

      const task = [...rawMyTasks, ...rawTasksDueSoon].find((t) => t.id === taskId);
      if (!task) {
        toast.error("Task not found");
        return;
      }

      const newDueDate = DateTime.now().plus({ days: 1 }).toISO();
      updateTask({
        body: {
          assignedToUserId: task.assignedToUserId,
          contactId: task.contactId,
          description: task.description,
          dueAt: newDueDate,
          priority: task.priority,
          reminderAt: task.reminderAt,
          status: task.status,
          title: task.title,
        },
        pathParams: { bubbleId: salesBubbleId, taskId },
      });
    },
    [updateTask, rawMyTasks, rawTasksDueSoon, toast, salesBubbleId],
  );

  return {
    daysUntilDue,
    filters,
    handleCompleteTask,
    handleCreateTask,
    handleDeleteTask,
    handleReassignTask,
    handleSnoozeTask,
    myTasks,
    setDaysUntilDue,
    setFilters,
    tasksDueSoon,
    tasksDueSoonStatus,
  };
};
